import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  IconButton,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import HomeIcon from '@mui/icons-material/Home'

import {
  addToNavHistory,
  goBackInHistory,
  goForwardInHistory,
  navigateToBreadcrumb,
} from '../../actions/navigationHistory'
import { setPageTitle } from '../../actions/pageTitle'

const NavBreadcrumbs = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const pageTitle = useSelector((state) => state.pageTitle)
  const navHistory = useSelector((state) => state.navigationHistory)

  const { history, currentIndex } = navHistory
  const canGoBack = currentIndex > 0
  const canGoForward = currentIndex < history.length - 1

  // Track navigation changes
  useEffect(() => {
    dispatch(addToNavHistory(location.pathname, pageTitle))
  }, [location.pathname, pageTitle, dispatch])

  const handleBack = () => {
    if (canGoBack) {
      dispatch(goBackInHistory())
      const previousEntry = history[currentIndex - 1]
      navigate(previousEntry.route)
      dispatch(setPageTitle(previousEntry.title))
    }
  }

  const handleForward = () => {
    if (canGoForward) {
      dispatch(goForwardInHistory())
      const nextEntry = history[currentIndex + 1]
      navigate(nextEntry.route)
      dispatch(setPageTitle(nextEntry.title))
    }
  }

  const handleBreadcrumbClick = (index) => {
    const targetEntry = history[index]
    if (targetEntry) {
      // If clicking Dashboard (index 0), this will trigger ADD_TO_NAV_HISTORY with route '/'
      // which will reset the history in the reducer
      dispatch(navigateToBreadcrumb(index))
      navigate(targetEntry.route)
      dispatch(setPageTitle(targetEntry.title))
    }
  }

  // Get breadcrumbs to display
  // Always show Dashboard (index 0) as first breadcrumb
  // Then show the most recent pages up to the current page
  const getBreadcrumbsToDisplay = () => {
    const maxTotal = 5 // Dashboard + up to 4 more pages

    if (history.length <= maxTotal) {
      // Show all if we have less than max
      return {
        breadcrumbs: history.slice(0, currentIndex + 1),
        hasHidden: false,
      }
    }

    // Always include Dashboard (index 0)
    const dashboardEntry = history[0]

    // Calculate how many recent pages to show after Dashboard
    const remainingSlots = maxTotal - 1 // -1 for Dashboard
    const startIndex = Math.max(1, currentIndex - remainingSlots + 1)

    const recentPages = history.slice(startIndex, currentIndex + 1)

    return {
      breadcrumbs: [dashboardEntry, ...recentPages],
      hasHidden: startIndex > 1,
    }
  }

  const { breadcrumbs: displayBreadcrumbs, hasHidden: hasHiddenBreadcrumbs } =
    getBreadcrumbsToDisplay()

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
      <Tooltip title='Go Back' arrow>
        <span>
          <IconButton
            color='inherit'
            onClick={handleBack}
            disabled={!canGoBack}
            size='small'
          >
            <ArrowBackIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title='Go Forward' arrow>
        <span>
          <IconButton
            color='inherit'
            onClick={handleForward}
            disabled={!canGoForward}
            size='small'
          >
            <ArrowForwardIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Only show breadcrumbs on desktop/tablet, hide on mobile */}
      {!isMobile && (
        <Breadcrumbs
          separator={
            <NavigateNextIcon fontSize='small' sx={{ color: '#FFAA00' }} />
          }
          sx={{
            color: 'white',
            ml: 1,
            '& .MuiBreadcrumbs-separator': {
              mx: 0.5,
            },
          }}
        >
          {displayBreadcrumbs.map((item, displayIndex) => {
            // Map display index to actual history index
            let actualIndex
            if (displayIndex === 0) {
              // First item is always Dashboard at index 0
              actualIndex = 0
            } else {
              // Find the actual index for subsequent items
              // Account for potentially hidden breadcrumbs
              const offsetFromEnd = displayBreadcrumbs.length - 1 - displayIndex
              actualIndex = currentIndex - offsetFromEnd
            }

            const isLast = actualIndex === currentIndex
            const isDashboard = item.route === '/'

            // Show ellipsis between Dashboard and next breadcrumb if there are hidden pages
            if (displayIndex === 1 && hasHiddenBreadcrumbs) {
              return (
                <Typography
                  key='ellipsis'
                  color='inherit'
                  sx={{ fontSize: '0.875rem' }}
                >
                  ...
                </Typography>
              )
            }

            if (isLast) {
              return (
                <Typography
                  key={actualIndex}
                  color='inherit'
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    color: '#FFAA00',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {isDashboard ? <HomeIcon fontSize='small' /> : item.title}
                </Typography>
              )
            }

            return (
              <Link
                key={actualIndex}
                color='inherit'
                href='#'
                onClick={(e) => {
                  e.preventDefault()
                  handleBreadcrumbClick(actualIndex)
                }}
                sx={{
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#FFAA00',
                  },
                }}
              >
                {isDashboard ? <HomeIcon fontSize='small' /> : item.title}
              </Link>
            )
          })}
        </Breadcrumbs>
      )}
    </Box>
  )
}

export default NavBreadcrumbs
